"use client";

import { createContext, useContext, useCallback, useRef, useState } from "react";

interface LikeState {
  liked: boolean;
  likesCount: number;
}

interface LikesContextType {
  notifyLikeChange: (itemId: string, itemType: "post" | "listing", liked: boolean, likesCount: number) => void;
  subscribeLikeChange: (callback: (itemId: string, itemType: "post" | "listing", liked: boolean, likesCount: number) => void) => () => void;
  getLikeState: (itemId: string, itemType: "post" | "listing") => LikeState | null;
  setLikeState: (itemId: string, itemType: "post" | "listing", liked: boolean, likesCount: number) => void;
}

const LikesContext = createContext<LikesContextType | undefined>(undefined);

export function LikesProvider({ children }: { children: React.ReactNode }) {
  const listenersRef = useRef<Set<(itemId: string, itemType: "post" | "listing", liked: boolean, likesCount: number) => void>>(new Set());
  const [likeStates, setLikeStates] = useState<Map<string, LikeState>>(new Map());

  const getStateKey = (itemId: string, itemType: "post" | "listing") => `${itemType}-${itemId}`;

  const getLikeState = useCallback((itemId: string, itemType: "post" | "listing") => {
    return likeStates.get(getStateKey(itemId, itemType)) || null;
  }, [likeStates]);

  const setLikeState = useCallback((itemId: string, itemType: "post" | "listing", liked: boolean, likesCount: number) => {
    const key = getStateKey(itemId, itemType);
    setLikeStates(prev => {
      const newMap = new Map(prev);
      newMap.set(key, { liked, likesCount });
      return newMap;
    });
  }, []);

  const notifyLikeChange = useCallback((itemId: string, itemType: "post" | "listing", liked: boolean, likesCount: number) => {
    // Update global state
    setLikeState(itemId, itemType, liked, likesCount);
    
    // Notify all listeners
    listenersRef.current.forEach(listener => {
      listener(itemId, itemType, liked, likesCount);
    });
  }, [setLikeState]);

  const subscribeLikeChange = useCallback((callback: (itemId: string, itemType: "post" | "listing", liked: boolean, likesCount: number) => void) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  return (
    <LikesContext.Provider value={{ notifyLikeChange, subscribeLikeChange, getLikeState, setLikeState }}>
      {children}
    </LikesContext.Provider>
  );
}

export function useLikes() {
  const context = useContext(LikesContext);
  if (!context) {
    throw new Error("useLikes must be used within a LikesProvider");
  }
  return context;
}
